import React, { FC, useEffect, useState } from 'react';
import { PageTitle } from '../../layout/core';
import { useParams } from 'react-router-dom';
import { GoBackBtn } from '../header/GoBackBtn';
import { CustomKeyClaims } from './CustomKeyClaims';
import { ButtonComponent } from '../ButtonComponent';
import {
	fetchApiClientKeysDetail,
	fetchResourceList, setApiClientKeysDetails,
	useApiClientContext
} from '../../store/api-client-context';
import { Spinner } from '../Spinner';
import { useFormik } from 'formik';
import {
	getCustomKeyDetailsSchema, getFormattedCustomKeyFormFields, getFormattedCustomKeyFormFValues, IFormattedProps,
	initialCustomKeyValues
} from '../../constants/custom-key/custom-key-form';
import { isEqual } from 'lodash';
import { successAlert } from '../ToastMessage';
import { CustomKeyDetailsSecretKeysSection } from './CustomKeyDetailsSecretKeysSection';
import { CustomKeyDetailsForm } from './CustomKeyDetailsForm';
import { shallowEqual, useSelector } from 'react-redux';
import { IAuthState } from '../../redux/AuthRedux';
import { RootState } from '../../../setup';
import { adminBreadCrumbs } from '../../routing/adminRoutes';
import { CustomKeyDetailsLoginForm } from './CustomKeyDetailsLooginForm';

export const CustomKeyDetails: FC = () => {
	const { clientId } = useParams();

	const [isLoginVisible, setIsLoginModalVisible] = useState<boolean>(false);

	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const userData = authUser.userData;

	const {
		apiClientContextState: {
			apiClientKeysDetails,
			isApiClientKeysDetailsLoading,
			isResourceListLoading,
			isSavingApiClientKeysDetailsLoading
		},
		dispatch
	} = useApiClientContext();

	const [selectedPermissions, setSelectedPermissions] = useState<{ [p: string]: string }>({});

	const formik = useFormik({
		initialValues: initialCustomKeyValues,
		validationSchema: getCustomKeyDetailsSchema,
		onSubmit: () => void 0
	});

	useEffect(() => {
		const offset = 5 * 60 * 1000; // 5 min
		if (userData?.loginDate && (new Date().getTime() - new Date(userData?.loginDate).getTime() <= offset )) {
			void fetchApiClientKeysDetail(dispatch, clientId, formik);
		} else {
			setIsLoginModalVisible(true);
		}
	}, []);

	useEffect(() => {
		// void fetchApiClientKeysDetail(dispatch, clientId, formik);
		void fetchResourceList(dispatch);
	}, []);

	if (isResourceListLoading || isApiClientKeysDetailsLoading) {
		return (
			<div className="card h-100">
				<div className="card-body">
					<Spinner pageLoader/>
				</div>
			</div>
		);
	}

	const handleSave = async (): Promise<void> => {
		formik.handleSubmit();
		if(!formik.isValid) return;
		const scopes = Object.values(selectedPermissions || {})?.filter(el => el);
		const formattedValues = getFormattedCustomKeyFormFields(formik.values);
		const body: IFormattedProps = {
			...formattedValues,
			apiClient: apiClientKeysDetails?.clientId,
			scopes
		};
		const formattedInitialValues = getFormattedCustomKeyFormFValues(apiClientKeysDetails);
		if (isEqual(body, formattedInitialValues)) {
			successAlert({
				title: 'Information was successfully saved!'
			});
		} else {
			await setApiClientKeysDetails(dispatch, body, formik);
		}
	};

	const closeLoginModal = (): void => setIsLoginModalVisible(false);

	return (
		<>
			<PageTitle breadcrumbs={adminBreadCrumbs}>
				{clientId === 'create-key' ? 'Create custom key' : clientId}
			</PageTitle>
			<div className="d-flex align-items-center justify-content-between mb-5">
				<GoBackBtn path="/admin/api-clients" />
				<ButtonComponent
					buttonName="Save"
					additionalClass="btn-sm w-100px"
					onButtonClick={():Promise<void> => !isSavingApiClientKeysDetailsLoading && handleSave()}
					type="submit"
					loading={isSavingApiClientKeysDetailsLoading}
				/>
			</div>

			<div className="card">
				<div className="card-body">

					<div className="d-xl-flex">
						<CustomKeyDetailsForm formik={formik} isEditMode={true}/>
						<CustomKeyDetailsSecretKeysSection />
					</div>

					<CustomKeyClaims setSelectedPermissions={setSelectedPermissions} isEditMode={true} />

				</div>
			</div>

			<CustomKeyDetailsLoginForm
				isLoginVisible={isLoginVisible}
				closeModal={closeLoginModal}
				formik={formik}
			/>
		</>
	);
};
